import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Particles } from "./Particles";
import { STATS } from "../../data/apps";

export const AppsHero = () => {
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      setOffset({
        x: (e.clientX / window.innerWidth - 0.5) * 24,
        y: (e.clientY / window.innerHeight - 0.5) * 24,
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section id="hero" className="relative grain min-h-screen overflow-hidden bg-tt-black" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ x: offset.x * 0.5, y: offset.y * 0.5 }}>
        <img src="/assets/hero.png" alt="" className="h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-tt-black/60 via-tt-black/80 to-tt-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_0%,_rgba(5,5,5,0.7)_70%)]" />
      </motion.div>

      {/* Giant ghosted wireframe horns watermark */}
      <motion.img
        src="/assets/horns_wire_black.png"
        alt=""
        aria-hidden="true"
        style={{ x: offset.x * 1.3, y: offset.y * 1.3 }}
        className="pointer-events-none absolute left-1/2 top-[42%] w-[1100px] max-w-none -translate-x-1/2 -translate-y-1/2 opacity-[0.22] mix-blend-screen"
      />

      <Particles />

      <div className="pointer-events-none absolute left-1/2 top-1/3 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-tt-gold/10 blur-[150px]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center px-6 pt-32 pb-16 text-center">
        {/* Big chrome logo mark */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          style={{ x: offset.x * 0.8, y: offset.y * 0.8 }}
          className="relative mb-2"
        >
          <div className="absolute left-1/2 top-1/2 h-48 w-48 -translate-x-1/2 -translate-y-1/2 rounded-full bg-tt-gold/15 blur-[60px]" />
          <img
            src="/assets/horns_chrome_t.png"
            alt="Taurus Technology"
            className="relative h-40 w-auto animate-float object-contain drop-shadow-[0_18px_40px_rgba(0,0,0,0.8)] sm:h-56"
            data-testid="hero-logo-mark"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full glass px-4 py-2"
          data-testid="hero-slogan-badge"
        >
          <span className="h-2 w-2 animate-pulse rounded-full bg-tt-gold" />
          <span className="font-grotesk text-xs uppercase tracking-[0.25em] text-tt-silver/80">
            Building Software For People Who Perform
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
          data-testid="hero-headline"
        >
          <span className="metallic-text">Pro Apps Built</span>
          <br />
          <span className="text-tt-silver/90">For The </span>
          <span className="gold-text">Live Stage</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-8 max-w-2xl text-base leading-relaxed text-tt-silver/65 sm:text-lg"
        >
          A suite of music production and live performance apps for the people who mix, play, and
          perform live — engineered by Taurus Technology to keep your show locked, tight, and
          unstoppable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-10"
        >
          <button
            onClick={() => document.querySelector("#apps")?.scrollIntoView({ behavior: "smooth" })}
            className="group inline-flex items-center gap-2 rounded-full bg-tt-gold px-8 py-3.5 font-grotesk text-sm font-semibold text-tt-black transition-all duration-300 hover:bg-tt-goldsoft hover:shadow-[0_0_40px_-6px_rgba(200,162,90,0.7)]"
            data-testid="hero-browse-button"
          >
            Explore the Apps
            <ChevronDown size={18} className="transition-transform group-hover:translate-y-0.5" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75 }}
          className="mt-16 grid w-full max-w-2xl grid-cols-2 gap-px overflow-hidden rounded-2xl glass sm:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="px-4 py-6 text-center">
              <div className="font-display text-2xl font-bold gold-text sm:text-3xl">{s.value}</div>
              <div className="mt-1 font-grotesk text-[11px] uppercase tracking-wider text-tt-silver/50">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        onClick={() => document.querySelector("#apps")?.scrollIntoView({ behavior: "smooth" })}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{ opacity: { delay: 1 }, y: { repeat: Infinity, duration: 2 } }}
        className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-tt-silver/50 hover:text-tt-gold"
        aria-label="Scroll down"
      >
        <ChevronDown size={26} />
      </motion.button>
    </section>
  );
};
